(function (root, factory) {
  const core = typeof module === 'object' && module.exports
    ? require('./catalog-illustrator-core.js')
    : root.QCatalogIllustratorCore;
  const api = factory(core);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.QCatalogIllustratorUI = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function (core) {
  'use strict';

  function mount(sectionRoot, { catalog, onChange } = {}) {
    if (!sectionRoot || !catalog || typeof catalog.subscribe !== 'function' || typeof onChange !== 'function' || !core) {
      throw new TypeError('QCatalogIllustratorUI.mount requires a section root, catalog and onChange');
    }

    const root = sectionRoot.matches?.('[data-catalog-illustrator]')
      ? sectionRoot : sectionRoot.querySelector('[data-catalog-illustrator]');
    if (!root) return { destroy() {} };

    const select = (name) => root.querySelector(`[data-illustrator-${name}]`);
    const trigger = select('open');
    const dialog = select('dialog');
    const closeButton = select('close');
    const search = select('search');
    const searchToggle = select('search-toggle');
    const searchPanel = select('search-panel');
    const options = select('options');
    const more = select('more');
    const preferredNames = select('favorites')?.textContent?.split(/\r?\n/) || [];
    const message = select('message');
    const retry = select('retry');
    const clear = select('clear');
    const apply = select('apply');
    const selected = select('selected');
    const count = select('count');
    if (![trigger, dialog, closeButton, search, searchToggle, searchPanel, options, more, message, retry, clear, apply, selected, count].every(Boolean)) {
      throw new TypeError('QCatalogIllustratorUI markup is incomplete');
    }

    const copy = (key) => select(`text-${key}`)?.textContent?.trim() || '';
    const listeners = [];
    const listen = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      listeners.push(() => target.removeEventListener(type, handler, options));
    };
    let destroyed = false;
    let active = new Set();
    let draft = new Set();
    let facets = [];
    let labels = new Map();
    let phase = 'idle';
    let signature = '';
    let restoreFocus = false;
    let expanded = false;

    const isOpen = () => dialog.matches(':popover-open');

    function position() {
      if (!isOpen()) return;
      const rect = trigger.getBoundingClientRect();
      const width = Math.min(rect.width, window.innerWidth - 24);
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const flip = below < 280 && above > below;
      const available = flip ? above : below;
      const maxHeight = Math.max(120, Math.min(520, available - 8));
      dialog.style.setProperty('--q-illustrator-max-height', `${maxHeight}px`);
      dialog.style.setProperty('--q-illustrator-width', `${width}px`);
      const height = dialog.getBoundingClientRect().height;
      const top = flip ? rect.top - height - 8 : rect.bottom + 8;
      dialog.style.setProperty('--q-illustrator-left', `${left}px`);
      dialog.style.setProperty('--q-illustrator-top', `${Math.max(8, Math.min(top, window.innerHeight - height - 8))}px`);
    }

    function drawSelected() {
      selected.replaceChildren();
      count.hidden = active.size === 0;
      count.textContent = String(active.size);
      trigger.classList.toggle('is-active', active.size > 0);
      selected.hidden = active.size === 0;
      for (const key of active) {
        const label = labels.get(key) || key;
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'q-catalog-illustrator__pill';
        pill.dataset.illustratorKey = key;
        pill.setAttribute('aria-label', `${copy('remove')} ${label}`.trim());
        const text = document.createElement('span');
        text.textContent = label;
        const close = document.createElement('span');
        close.setAttribute('aria-hidden', 'true');
        close.textContent = '×';
        pill.append(text, close);
        selected.append(pill);
      }
    }

    function drawOptions() {
      const query = core.nameKey(search.value);
      const visible = query ? facets.filter(({ label }) => core.nameKey(label).includes(query))
        : expanded ? facets : facets.filter(({ key }, index) => index < 5 || draft.has(key));
      more.hidden = Boolean(query) || expanded || visible.length === facets.length;
      more.setAttribute('aria-expanded', String(expanded));
      message.textContent = facets.length ? (visible.length ? '' : copy('no-matches')) : copy('empty');
      const nextSignature = visible.map(({ key, label }) => `${key}\u0000${label}`).join('\u0001');
      requestAnimationFrame(position);
      if (nextSignature === signature) {
        for (const input of options.querySelectorAll('input[type="checkbox"]')) {
          input.checked = draft.has(input.value);
        }
        return;
      }
      signature = nextSignature;
      const focusedKey = dialog.contains(document.activeElement)
        ? document.activeElement.closest('[data-illustrator-key]')?.dataset.illustratorKey : null;
      const fragment = document.createDocumentFragment();
      for (const facet of visible) {
        const row = document.createElement('label');
        row.className = 'q-catalog-illustrator__option';
        row.dataset.illustratorLabel = facet.label;
        row.dataset.illustratorKey = facet.key;
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.value = facet.key;
        checkbox.checked = draft.has(facet.key);
        const text = document.createElement('span');
        text.textContent = facet.label;
        row.append(checkbox, text);
        fragment.append(row);
      }
      options.replaceChildren(fragment);
      if (focusedKey) {
        Array.from(options.querySelectorAll('label')).find((row) => row.dataset.illustratorKey === focusedKey)?.querySelector('input')?.focus();
      }
    }

    function render(state, opening = false) {
      if (destroyed || !state) return;
      phase = state.phase || 'idle';
      if (!isOpen() && !opening) return;
      if (phase === 'ready') {
        facets = core.prioritizeFacets(core.facets(catalog.getProducts()), preferredNames);
        for (const facet of facets) labels.set(facet.key, facet.label);
      }
      const ready = phase === 'ready';
      options.hidden = !ready || facets.length === 0;
      search.disabled = !ready || facets.length === 0;
      searchToggle.disabled = search.disabled;
      retry.hidden = phase !== 'error';
      apply.disabled = !ready;
      clear.disabled = !ready;
      if (ready) {
        drawOptions();
      } else {
        more.hidden = true;
        message.textContent = phase === 'error' ? copy('error') : copy('loading');
      }
      requestAnimationFrame(position);
    }

    function prepareOpen() {
      if (destroyed) return;
      draft = new Set(active);
      expanded = false;
      search.value = '';
      searchPanel.hidden = true;
      searchToggle.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      render(catalog.getState(), true);
      Promise.resolve().then(() => catalog.ensure()).catch(() => {
        // The catalog publishes its error state; a retry remains available.
      });
    }

    function close(focus = true) {
      if (!isOpen()) return;
      restoreFocus = focus;
      dialog.hidePopover();
    }

    listen(closeButton, 'click', () => close());
    listen(document, 'keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) restoreFocus = true;
    });
    listen(dialog, 'beforetoggle', (event) => {
      if (event.newState === 'open') prepareOpen();
    });
    listen(dialog, 'toggle', (event) => {
      if (event.newState === 'open') {
        if (destroyed) return;
        position();
        (searchToggle.disabled ? closeButton : searchToggle).focus();
        return;
      }
      if (event.newState !== 'closed') return;
      draft = new Set(active);
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus && !destroyed && trigger.isConnected) trigger.focus();
      restoreFocus = false;
    });
    listen(search, 'input', drawOptions);
    listen(searchToggle, 'click', () => {
      const opening = searchPanel.hidden;
      searchPanel.hidden = !opening;
      searchToggle.setAttribute('aria-expanded', String(opening));
      if (!opening) {
        search.value = '';
        drawOptions();
      }
      (opening ? search : searchToggle).focus();
      requestAnimationFrame(position);
    });
    listen(more, 'click', () => {
      const previousKeys = new Set(Array.from(options.children, row => row.dataset.illustratorKey));
      expanded = true;
      drawOptions();
      const firstNew = Array.from(options.children).find(row => !previousKeys.has(row.dataset.illustratorKey));
      firstNew?.querySelector('input')?.focus();
    });
    listen(options, 'change', (event) => {
      const input = event.target;
      if (!input.matches('input[type="checkbox"]')) return;
      if (input.checked) draft.add(input.value);
      else draft.delete(input.value);
    });
    listen(clear, 'click', () => {
      draft.clear();
      for (const input of options.querySelectorAll('input[type="checkbox"]')) input.checked = false;
    });
    listen(apply, 'click', () => {
      if (phase !== 'ready') return;
      active = new Set(draft);
      drawSelected();
      onChange(new Set(active));
      close();
    });
    listen(retry, 'click', () => {
      Promise.resolve().then(() => catalog.ensure()).catch(() => {});
    });
    listen(selected, 'click', (event) => {
      const pill = event.target.closest('[data-illustrator-key]');
      if (!pill || !selected.contains(pill)) return;
      const index = Array.from(selected.children).indexOf(pill);
      active.delete(pill.dataset.illustratorKey);
      drawSelected();
      onChange(new Set(active));
      const next = selected.children[Math.min(index, selected.children.length - 1)];
      (next || trigger).focus();
    });
    listen(window, 'resize', position);
    listen(window, 'scroll', position, true);
    listen(document, 'shopify:section:unload', (event) => {
      if (event.target === sectionRoot || event.target.contains(sectionRoot)) destroy();
    });
    const unsubscribe = catalog.subscribe(render);

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      close(false);
      trigger.setAttribute('aria-expanded', 'false');
      if (typeof unsubscribe === 'function') unsubscribe();
      for (const remove of listeners) remove();
    }

    return { destroy };
  }

  return { mount };
});
